import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  LayoutDashboard,
  Database,
  Lock,
  Table,
  GitFork,
  LineChart,
  Radio,
  Wrench,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
  Layers,
  TableOfContents,
  HardDrive,
  Cpu,
  Activity,
  Users,
  UserCheck,
  Terminal,
  Archive,
  Menu,
  X,
  Search,
  Check,
  HardDriveDownload,
} from 'lucide-react';
import DBTypeIcon from '../../../components/DBTypeIcon';

export interface SidebarItem {
  id: string;
  label: string;
  icon: LucideIcon;
  subItems?: any[];
}

export interface SidebarGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  subItems?: any[];
}

interface DBSidebarProps {
  activeSidebar: string;
  onSelectSidebar: (id: string) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
  dbType?: string;
}

export const sidebarItems: SidebarItem[] = [
  { id: 'topology', label: 'Topology', icon: GitFork },
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'metrics', label: 'Server Performance', icon: LineChart },
  { id: 'instance_memory', label: 'Instance and Memory', icon: Cpu },
  { id: 'databases', label: 'Database Performance', icon: Database },
  { id: 'collections', label: 'Collections', icon: Layers },
  { id: 'backups', label: 'Backups', icon: Archive },
  { id: 'tempdb', label: 'Temporary DB', icon: Layers },
  { id: 'memory', label: 'Memory', icon: Cpu },
  { id: 'schedulers', label: 'Schedulers', icon: Activity },
  { id: 'file_io', label: 'File IO', icon: HardDrive },
  { id: 'indexes', label: 'Indexes', icon: TableOfContents },
  { id: 'storage', label: 'Storage & Maintenance', icon: HardDrive },
  { id: 'ingestion', label: 'Ingestion', icon: HardDriveDownload },
  { id: 'esindices', label: 'Indices', icon: Layers },
  { id: 'blocking', label: 'Queries', icon: Lock },
  { id: 'sessions_locks', label: 'Sessions and Locks', icon: Users },
  { id: 'users', label: 'Users & Logins', icon: UserCheck },
  { id: 'user_queries', label: 'User Query Statistics', icon: Users },
  { id: 'schema', label: 'Schema', icon: Table },
  { id: 'connections', label: 'Active Connections', icon: Radio },
  { id: 'logs', label: 'Log Monitor', icon: Terminal },
  // { id: 'health', label: 'Agent Health', icon: ShieldCheck },
  { id: 'configuration', label: 'Configuration', icon: Wrench },
];

export const sidebarGroups: SidebarGroup[] = sidebarItems;

export const getFilteredSidebarItems = (dbType?: string): SidebarItem[] => {
  const dbLower = (dbType || '').toLowerCase();
  const isES = ['opensearch', 'elasticsearch'].includes(dbLower);
  const isMongoDB = ['mongodb', 'mongo'].includes(dbLower);
  const isMSSQL = ['mssql', 'sqlserver', 'sql server', 'sql_server'].includes(dbLower) || dbLower.includes('mssql');
  const isMySQL = ['mysql', 'mariadb'].includes(dbLower) || dbLower.includes('mysql');
  const isOracle = ['oracle'].includes(dbLower);
  const isClickHouse =
    ['clickhouse', 'click_house', 'click house'].includes(dbLower) ||
    dbLower.includes('clickhouse');

  return sidebarItems
    .filter((item) => {
      if (item.id === 'instance_memory') {
        return isOracle;
      }
      if (item.id === 'sessions_locks') {
        return isOracle;
      }
      if (item.id === 'connections' && isOracle) {
        return false;
      }
      if (item.id === 'collections' || item.id === 'collections2') {
        return isMongoDB;
      }
      if (item.id === 'backups' || item.id === 'tempdb' || item.id === 'memory') {
        return isMSSQL;
      }
      if (item.id === 'schedulers') {
        return isMSSQL || isMySQL || isOracle;
      }
      if (item.id === 'esindices') {
        return isES;
      }
      if (item.id === 'ingestion') {
        return isClickHouse;
      }
      if (isES && ['blocking', 'databases', 'indexes', 'storage', 'collections', 'collections2', 'backups'].includes(item.id)) {
        return false;
      }
      if (isMongoDB && ['esindices', 'backups'].includes(item.id)) {
        return false;
      }
      return true;
    })
    .map((item) => {
      if (item.id === 'storage' && isClickHouse) {
        return {
          ...item,
          label: 'Storage and Parts',
        };
      }
      return item;
    });
};

/**
 * Mobile Horizontal Tab Bar for screens < lg
 */
export interface DBMobileTabsBarProps {
  activeSidebar: string;
  onSelectSidebar: (id: string) => void;
  dbType?: string;
  onOpenDrawer: () => void;
}

export const DBMobileTabsBar: React.FC<DBMobileTabsBarProps> = ({
  activeSidebar,
  onSelectSidebar,
  dbType,
  onOpenDrawer,
}) => {
  const visibleItems = getFilteredSidebarItems(dbType);
  const activeItemRef = useRef<HTMLButtonElement | null>(null);

  // Auto scroll the active tab into view horizontally
  useEffect(() => {
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [activeSidebar]);

  return (
    <div className="lg:hidden shrink-0 flex items-center gap-1.5 bg-white/95 dark:bg-gray-900/95 border-b border-gray-200 dark:border-gray-800/90 py-1.5 px-0.5 min-w-0">
      {/* Mobile Drawer Trigger */}
      <button
        onClick={onOpenDrawer}
        type="button"
        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white rounded-lg border border-gray-300 dark:border-gray-700 text-xs font-semibold shrink-0 transition-colors shadow-sm cursor-pointer"
        title="View All Tabs"
      >
        <Menu className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400 shrink-0" />
        <span className="hidden xs:inline text-[11px]">Tabs</span>
      </button>

      {/* Horizontal Scrollable Pills */}
      <div className="flex-1 flex items-center gap-1.5 overflow-x-auto custom-scroll no-scrollbar py-0.5">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeSidebar === item.id;

          return (
            <button
              key={item.id}
              ref={isActive ? activeItemRef : null}
              type="button"
              onClick={() => onSelectSidebar(item.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap shrink-0 transition-all cursor-pointer ${isActive
                  ? 'bg-orange-600 border border-orange-500/50 text-white font-bold shadow-md'
                  : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-800/80 dark:hover:bg-gray-800 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white border border-gray-200 dark:border-gray-700/60'
                }`}
            >
              <Icon className={`w-3.5 h-3.5 shrink-0 ${isActive ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Mobile Slide-In Navigation Drawer Modal
 */
export interface DBMobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  activeSidebar: string;
  onSelectSidebar: (id: string) => void;
  dbType?: string;
}

export const DBMobileDrawer: React.FC<DBMobileDrawerProps> = ({
  isOpen,
  onClose,
  activeSidebar,
  onSelectSidebar,
  dbType,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const visibleItems = getFilteredSidebarItems(dbType);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const filteredItems = visibleItems.filter((item) =>
    item.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-200"
        onClick={onClose}
      />

      {/* Slide-over Drawer Panel */}
      <div className="relative w-[85%] max-w-xs bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col h-full z-10 animate-in slide-in-from-left duration-200">
        {/* Drawer Header */}
        <div className="flex items-center justify-between p-3.5 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/60">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-orange-100 dark:bg-orange-600/20 border border-orange-200 dark:border-orange-500/30 flex items-center justify-center text-orange-600 dark:text-orange-400">
              <DBTypeIcon dbType={dbType} className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">Database Navigation</h3>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 capitalize">{dbType || 'Database'} cluster</p>
            </div>
          </div>
          <button
            onClick={onClose}
            type="button"
            className="p-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white bg-gray-100 hover:bg-gray-200 dark:bg-gray-800/80 dark:hover:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700/60 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search Filter */}
        <div className="p-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search views..."
              className="w-full bg-white dark:bg-gray-800/90 border border-gray-300 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-white"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Navigation List */}
        <div className="flex-1 overflow-y-auto custom-scroll p-2 space-y-1">
          {filteredItems.length === 0 ? (
            <div className="text-center py-8 text-xs text-gray-500 dark:text-gray-400">
              No matching tabs found
            </div>
          ) : (
            filteredItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSidebar === item.id;

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    onSelectSidebar(item.id);
                    onClose();
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-medium transition-colors cursor-pointer border ${isActive
                      ? 'bg-orange-600 border-orange-500/50 text-white font-bold shadow-md'
                      : 'border-transparent text-gray-700 hover:text-gray-900 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-white'
                    }`}
                >
                  <div className="flex items-center gap-2.5 truncate">
                    <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
                    <span className="truncate">{item.label}</span>
                  </div>
                  {isActive && <Check className="w-3.5 h-3.5 text-white shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

/**
 * Desktop Vertical DBSidebar (visible on lg and above)
 */
export const DBSidebar: React.FC<DBSidebarProps> = ({
  activeSidebar,
  onSelectSidebar,
  isOpen = true,
  onToggleOpen,
  dbType,
}) => {
  const [hoveredTab, setHoveredTab] = React.useState<{ label: string; top: number } | null>(null);
  const visibleItems = getFilteredSidebarItems(dbType);

  return (
    <>
      <aside className="w-full space-y-1 select-none flex flex-col h-full">
        {/* Sidebar Header with Collapse / Expand Toggle */}
        <div
          className={`flex items-center pb-2 mb-1 border-b border-gray-200 dark:border-gray-800/80 ${isOpen ? 'justify-between px-1' : 'justify-center'
            }`}
        >
          {isOpen && (
            <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Navigation
            </span>
          )}
          {onToggleOpen && (
            <button
              onClick={onToggleOpen}
              className="p-1 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-800/70 dark:hover:bg-gray-700/80 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white border border-gray-300 dark:border-gray-700/60 transition-all shadow-sm cursor-pointer"
              title={isOpen ? 'Collapse Sidebar' : 'Expand Sidebar'}
            >
              {isOpen ? <ChevronLeft className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>

        {/* Direct Flat List of All Items */}
        <div className="space-y-0.5 flex-1">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSidebar === item.id;

            return (
              <button
                key={item.id}
                onMouseEnter={(e) =>
                  !isOpen &&
                  setHoveredTab({ label: item.label, top: e.currentTarget.getBoundingClientRect().top })
                }
                onMouseLeave={() => setHoveredTab(null)}
                onClick={() => {
                  onSelectSidebar(item.id);
                  setHoveredTab(null);
                }}
                title={!isOpen ? item.label : undefined}
                className={`w-full flex items-center gap-2 rounded-lg text-xs transition-colors duration-150 border cursor-pointer ${isOpen ? 'px-2.5 py-1.5 justify-start' : 'px-2 py-1.5 justify-center'
                  } ${isActive
                    ? 'bg-orange-600 border-orange-500/40 text-white font-bold shadow-md'
                    : 'border-transparent hover:bg-gray-100 dark:hover:bg-gray-800/50 text-gray-700 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white font-medium'
                  }`}
              >
                <Icon className={`w-3.5 h-3.5 shrink-0 ${isActive ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
                {isOpen && <span className="truncate">{item.label}</span>}
              </button>
            );
          })}
        </div>
      </aside>

      {/* Floating Tooltip when Sidebar is Collapsed */}
      {!isOpen && hoveredTab && (
        <div
          className="fixed z-[9999] px-2.5 py-1.5 bg-orange-600 text-white text-[11px] font-bold rounded-lg shadow-2xl whitespace-nowrap pointer-events-none transition-opacity duration-200 border border-orange-500/50"
          style={{ left: '4.25rem', top: hoveredTab.top + 2 }}
        >
          {hoveredTab.label}
          <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-orange-600 rotate-45 rounded-sm"></div>
        </div>
      )}
    </>
  );
};

export default DBSidebar;
